import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { ListItem } from '@/types/lists';
import { useListsStore } from '@/store/listsStore';
import { Calendar, Clock } from 'lucide-react';

// Validation schema for item editing
const itemSchema = z.object({
  content: z.string().min(1, 'Content is required').max(500, 'Content must be less than 500 characters'),
  description: z.string().optional(),
  dueDate: z.string().optional(),
  estimatedMinutes: z.number().min(0, 'Estimated minutes must be positive').optional(),
}).refine((data) => {
  // Due date must be in the future
  if (data.dueDate) {
    const dueDateTime = new Date(data.dueDate).getTime();
    const now = new Date().getTime();
    return dueDateTime > now;
  }
  return true;
}, {
  message: "Due date must be in the future",
  path: ["dueDate"]
});

type ItemFormInputs = z.infer<typeof itemSchema>;

interface ItemEditFormProps {
  isOpen: boolean;
  onClose: () => void;
  listId: string;
  item: ListItem;
}


export function ItemEditForm({
  isOpen,
  onClose,
  listId,
  item
}: ItemEditFormProps) {
  const { updateItem } = useListsStore();
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue
  } = useForm<ItemFormInputs>({
    resolver: zodResolver(itemSchema),
    defaultValues: {
      content: '',
      description: '',
      dueDate: '',
      estimatedMinutes: undefined,
    }
  });

  // Update form values when item data changes
  useEffect(() => {
    if (item && isOpen) {
      setValue('content', item.content);
      setValue('description', item.description || '');
      setValue('dueDate', item.dueDate ? 
        new Date(item.dueDate).toISOString().split('T')[0] : '');
      setValue('estimatedMinutes', item.estimatedMinutes);
    }
  }, [item, isOpen, setValue]);

  const handleFormSubmit = async (data: ItemFormInputs) => {
    try {
      const updates: Partial<ListItem> = {
        content: data.content,
        description: data.description || undefined,
        dueDate: data.dueDate ? new Date(data.dueDate) : undefined,
        estimatedMinutes: data.estimatedMinutes,
        updatedAt: new Date()
      };

      await updateItem(listId, item.id, updates);
      handleClose();
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      title="Edit Item"
      description="Update item details and properties"
      scrollBehavior="inside"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          {/* Content */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Content *</label>
            <Input
              {...register('content')}
              placeholder="What needs to be done?"
              error={errors.content?.message}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <textarea
              {...register('description')}
              placeholder="Additional details about this item..."
              className="w-full min-h-[80px] px-3 py-2 text-sm border border-input rounded-md bg-background ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            />
          </div>

          {/* Due Date and Estimated Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <label className="text-sm font-medium">Due Date</label>
              </div>
              <Input
                {...register('dueDate')}
                type="date"
                min={new Date().toISOString().split('T')[0]}
                error={errors.dueDate?.message}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <label className="text-sm font-medium">Estimated Time (minutes)</label>
              </div>
              <Input
                {...register('estimatedMinutes', { valueAsNumber: true })}
                type="number"
                min="0"
                placeholder="15"
              />
            </div>
          </div>


      </form>

      {/* Form Actions - Fixed at bottom */}
      <div className="flex justify-end items-center pt-4 border-t mt-4 bg-background sticky bottom-0">
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            onClick={handleSubmit(handleFormSubmit)}
          >
            {isSubmitting ? 'Saving...' : 'Update Item'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}